// --- Dependencies --------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePopup from "@/hooks/usePopup";
import useStore from "@/hooks/useStore";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyBranchAddonItem, TyCouponPreview, TySubscription, TySubscriptionConfig, TySubscriptionPeriod } from "@/types";
import type { TyActiveStore } from "@/hooks/useStore/types";
import Skeleton from "@/UI/atoms/Skeleton";
import CouponBadge from "@/UI/components/CouponBadge";
import CouponInput from "@/UI/inputs/CouponInput";
import ConfirmSubscriptionPopup from "@/UI/popups/ConfirmSubscriptionPopup";
import PurchaseBranchAddonPopup from "@/UI/popups/PurchaseBranchAddonPopup";
import "./styles.css";

// --- Helpers ------------------------------------------------------------------------------------

function formatDate(dateStr: string | null | undefined, lang: string): string {
    if (!dateStr) return "--";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "--";
    return date.toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US", { day: "numeric", month: "long", year: "numeric" });
}

function daysRemaining(endDate: string | null | undefined): number {
    if (!endDate) return 0;
    const diff = new Date(endDate).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / 86_400_000));
}

const WHATSAPP_SVG = (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.13-2.9-7.01A9.817 9.817 0 0 0 12.04 2zm.01 1.67c2.2 0 4.26.86 5.82 2.42a8.225 8.225 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.24 8.23-1.48 0-2.93-.39-4.19-1.15l-.3-.17-3.12.82.83-3.04-.2-.32a8.188 8.188 0 0 1-1.26-4.38c.01-4.54 3.7-8.24 8.25-8.24zM8.53 7.33c-.16 0-.43.06-.66.31-.22.25-.87.86-.87 2.07 0 1.22.89 2.39 1.01 2.56.13.17 1.75 2.67 4.25 3.73.59.27 1.05.43 1.41.55.59.19 1.13.16 1.56.1.48-.07 1.46-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.07-.1-.23-.16-.48-.27-.25-.14-1.47-.74-1.69-.82-.23-.08-.37-.12-.56.12-.16.25-.64.81-.78.97-.15.17-.29.19-.53.07-.26-.13-1.06-.39-2.02-1.25-.74-.66-1.23-1.48-1.38-1.73-.12-.24-.01-.39.11-.5.11-.11.27-.29.37-.44.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.11-.56-1.35-.77-1.84-.2-.48-.4-.42-.56-.43-.14 0-.3-.01-.47-.01z" />
    </svg>
);

// --- Sub-components -----------------------------------------------------------------------------

type PlanCardProps = {
    period: TySubscriptionPeriod;
    selected: boolean;
    prices: TySubscriptionConfig["prices"];
    t: ReturnType<typeof useTranslation>;
    onClick: () => void;
};

function PlanCard({ period, selected, prices, t, onClick }: PlanCardProps) {
    const periodLabel: Record<TySubscriptionPeriod, string> = {
        monthly: t.subscriptions.periodMonthly,
        quarterly: t.subscriptions.periodQuarterly,
        annually: t.subscriptions.periodAnnually,
    };
    const periodSuffix: Record<TySubscriptionPeriod, string> = {
        monthly: t.subscriptions.perMonth,
        quarterly: t.subscriptions.perQuarter,
        annually: t.subscriptions.perYear,
    };
    const savings: Record<TySubscriptionPeriod, number | null> = { monthly: null, quarterly: 15, annually: 40 };
    const saving = savings[period];

    return (
        <button className={`sub-plan-card${selected ? " sub-plan-card--selected" : ""}`} onClick={onClick} type="button">
            {saving !== null && (
                <span className="sub-plan-save-badge">
                    {t.subscriptions.saveLabel} {saving}%
                </span>
            )}
            <span className="sub-plan-period">{periodLabel[period]}</span>
            <span className="sub-plan-price">
                {prices[period].toLocaleString()}
                <span className="sub-plan-egp"> {t.subscriptions.egp}</span>
            </span>
            <span className="sub-plan-suffix">{periodSuffix[period]}</span>
        </button>
    );
}

type StatusBadgeProps = { status: TySubscription["status"]; t: ReturnType<typeof useTranslation> };

function StatusBadge({ status, t }: StatusBadgeProps) {
    const labels: Partial<Record<TySubscription["status"], string>> = {
        active: t.subscriptions.statusActive,
        grace: t.subscriptions.statusGrace,
        paused: t.subscriptions.statusPaused,
        pending: t.subscriptions.statusPending,
        coming: t.subscriptions.statusComing,
    };
    return <span className={`sub-status-badge sub-status-badge--${status}`}>{labels[status] ?? status}</span>;
}

type PaymentMethodCardProps = {
    receiver: TySubscriptionConfig["paymentReceivers"][number];
    t: ReturnType<typeof useTranslation>;
};

function PaymentMethodCard({ receiver, t }: PaymentMethodCardProps) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(receiver.phone).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="sub-payment-method">
            <img src={`/images/${receiver.method}.png`} alt={receiver.name} className="sub-payment-method-icon" />
            <div className="sub-payment-method-info">
                <span className="sub-payment-method-name">{receiver.name}</span>
                <span className="sub-payment-method-phone" dir="ltr">
                    {receiver.phone}
                </span>
            </div>
            <button className={`sub-payment-copy-btn${copied ? " sub-payment-copy-btn--copied" : ""}`} onClick={handleCopy} type="button">
                {copied ? t.subscriptions.copied : t.subscriptions.copyPhone}
            </button>
        </div>
    );
}

// --- Plan Selector View -------------------------------------------------------------------------

type PlanSelectorViewProps = {
    config: TySubscriptionConfig;
    t: ReturnType<typeof useTranslation>;
    onSuccess: () => void;
    initialBranches?: number;
    /** A store that has held a plan before is renewing — some coupons are valid for only one of the two. */
    isRenewal?: boolean;
};

function PlanSelectorView({ config, t, onSuccess, initialBranches = 1, isRenewal = false }: PlanSelectorViewProps) {
    const [selectedPeriod, setSelectedPeriod] = useState<TySubscriptionPeriod>("monthly");
    const [branches, setBranches] = useState(initialBranches);
    const [coupon, setCoupon] = useState<TyCouponPreview | null>(null);
    const { addComp, removeComp } = usePopup();

    const addonPrice = config.branchAddonPrices[selectedPeriod];
    const basePrice = config.prices[selectedPeriod] + addonPrice * (branches - 1);
    const discount = coupon?.discountAmount ?? 0;
    const totalPrice = basePrice - discount;

    const { data, loading, error, invoke } = useFetch<TyApiResponse<{ subscription: TySubscription }>>("/subscriptions", { method: "POST" }, false);

    useEffect(() => {
        if (data?.success) onSuccess();
    }, [data]);

    /** Subscribing is only a click away and cannot be undone from the dashboard, so it goes through a review step. */
    const handleSubmit = (e: React.SyntheticEvent) => {
        e.preventDefault();
        const name = "confirm-subscription";
        removeComp(name);
        addComp({
            name,
            Component: ConfirmSubscriptionPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: {
                period: selectedPeriod,
                branches,
                planPrice: config.prices[selectedPeriod],
                addonPrice,
                totalPrice,
                coupon,
                onConfirm: () => invoke({ method: "POST", data: { period: selectedPeriod, branches, ...(coupon ? { coupon: coupon.code } : {}) } }),
            },
        });
    };

    return (
        <div className="sub-view">
            <div className="sub-hero">
                <div className="sub-hero-icon">
                    <img src="/icons/star-active-icon.svg" alt="" width={28} height={28} />
                </div>
                <div>
                    <p className="sub-hero-title">{t.subscriptions.choosePlanTitle}</p>
                    <p className="sub-hero-subtitle">{t.subscriptions.choosePlanSubtitle}</p>
                </div>
            </div>

            <form className="sub-plan-form" onSubmit={handleSubmit}>
                <div className="sub-plan-grid">
                    {(["monthly", "quarterly", "annually"] as TySubscriptionPeriod[]).map((p) => (
                        <PlanCard
                            key={p}
                            period={p}
                            selected={selectedPeriod === p}
                            prices={config.prices}
                            t={t}
                            onClick={() => setSelectedPeriod(p)}
                        />
                    ))}
                </div>

                <div className="sub-branches-row">
                    <div className="sub-branches-info">
                        <span className="sub-branches-label">{t.subscriptions.branchesLabel}</span>
                        <span className="sub-branches-addon-note">
                            +{addonPrice} {t.subscriptions.egp} {t.subscriptions.branchAddonLabel}
                        </span>
                    </div>
                    <div className="sub-branches-counter">
                        <button
                            className="sub-branches-btn"
                            type="button"
                            onClick={() => setBranches((b) => Math.max(1, b - 1))}
                            disabled={branches <= 1}
                        >
                            −
                        </button>
                        <span className="sub-branches-count">{branches}</span>
                        <button className="sub-branches-btn" type="button" onClick={() => setBranches((b) => b + 1)}>
                            +
                        </button>
                    </div>
                </div>

                <CouponInput
                    action={isRenewal ? "renew" : "new"}
                    period={selectedPeriod}
                    branches={branches}
                    applied={coupon}
                    onApply={setCoupon}
                    disabled={loading}
                />

                <div className="sub-total-row">
                    <span className="sub-total-label">{t.subscriptions.totalLabel}</span>
                    <span className="sub-total-value">
                        {discount > 0 && <span className="sub-total-was">{basePrice.toLocaleString()}</span>}
                        {totalPrice.toLocaleString()} <span className="sub-total-egp">{t.subscriptions.egp}</span>
                    </span>
                </div>

                <p className="sub-pay-window">
                    <strong>{t.subscriptions.payWindowTitle}</strong>
                </p>

                {error && <p className="sub-error-msg">{error}</p>}

                <button className="sub-submit-btn" type="submit" disabled={loading}>
                    {loading ? <span className="sub-spinner" /> : t.subscriptions.subscribeButton}
                </button>
            </form>
        </div>
    );
}

// --- Payment Instructions View ------------------------------------------------------------------

type PaymentInstructionsViewProps = {
    pending: TySubscription;
    config: TySubscriptionConfig;
    t: ReturnType<typeof useTranslation>;
    store: TyActiveStore | null;
    lang: string;
};

function PendingSummaryCard({ pending, t }: { pending: TySubscription; t: ReturnType<typeof useTranslation> }) {
    const PERIOD_LABELS: Record<TySubscriptionPeriod, string> = {
        monthly: t.subscriptions.periodMonthly,
        quarterly: t.subscriptions.periodQuarterly,
        annually: t.subscriptions.periodAnnually,
    };

    return (
        <div className="sub-pending-summary">
            <StatusBadge status="pending" t={t} />
            <div className="sub-pending-amount-hero">
                <span className="sub-pending-amount-number">{pending.price.toLocaleString()}</span>
                <span className="sub-pending-amount-egp">{t.subscriptions.egp}</span>
            </div>
            <p className="sub-pending-amount-label">{t.subscriptions.amountLabel}</p>
            {pending.coupon && (
                <div className="sub-pending-coupon">
                    <CouponBadge code={pending.coupon.code} discount={pending.coupon.discount} savedAmount={pending.couponDiscount} compact />
                </div>
            )}
            <div className="sub-pending-card-divider" />
            <div className="sub-pending-meta">
                <div className="sub-pending-meta-item">
                    <span className="sub-pending-meta-label">{t.subscriptions.periodLabel}</span>
                    <span className="sub-pending-meta-value">{PERIOD_LABELS[pending.period]}</span>
                </div>
                <div className="sub-pending-meta-sep" />
                <div className="sub-pending-meta-item">
                    <span className="sub-pending-meta-label">{t.subscriptions.branchesLabel}</span>
                    <span className="sub-pending-meta-value">
                        {pending.branches} {t.subscriptions.branchCount}
                    </span>
                </div>
            </div>
        </div>
    );
}

function SlugCopyRow({ slug, t }: { slug: string; t: ReturnType<typeof useTranslation> }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(slug).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="sub-slug-row">
            <div className="sub-slug-info">
                <span className="sub-slug-label">{t.subscriptions.storeSlugLabel}</span>
                <span className="sub-slug-note">{t.subscriptions.storeSlugNote}</span>
            </div>
            <div className="sub-slug-copy" dir="ltr">
                <span className="sub-slug-value">{slug}</span>
                <button className={`sub-payment-copy-btn${copied ? " sub-payment-copy-btn--copied" : ""}`} onClick={handleCopy} type="button">
                    {copied ? t.subscriptions.copied : t.subscriptions.copyPhone}
                </button>
            </div>
        </div>
    );
}

/** The deadline is the client's only warning that an unpaid request disappears — it stays loud. */
function PayWindowNotice({ dueAt, lang, t }: { dueAt: string | null; lang: string; t: ReturnType<typeof useTranslation> }) {
    if (!dueAt) return null;

    return (
        <div className="sub-alert sub-alert--warning">
            <img src="/icons/timer-icon.svg" alt="" width={20} height={20} />
            <div>
                <p className="sub-alert-title">{t.subscriptions.payWindowTitle}</p>
                <p className="sub-alert-note">{t.subscriptions.payWindowNote.replace("{date}", formatDate(dueAt, lang))}</p>
            </div>
        </div>
    );
}

function PaymentInstructionsView({ pending, config, t, store, lang }: PaymentInstructionsViewProps) {
    const PERIOD_LABELS: Record<TySubscriptionPeriod, string> = {
        monthly: t.subscriptions.periodMonthly,
        quarterly: t.subscriptions.periodQuarterly,
        annually: t.subscriptions.periodAnnually,
    };

    const slug = store?.slug ?? "";
    const whatsAppPhone = config.whatsappPhone.replace("+", "");
    const requestedOn = formatDate(pending.createdAt, "ar");
    const message = encodeURIComponent(
        `طلب تفعيل اشتراك منيوكيت\nرابط المتجر: ${slug}\nتاريخ الطلب: ${requestedOn}\nالباقة: ${PERIOD_LABELS[pending.period]}\nالفروع: ${pending.branches} فرع\nالمبلغ: ${pending.price} ${t.subscriptions.egp}`
    );
    const whatsAppUrl = `https://wa.me/${whatsAppPhone}?text=${message}`;

    return (
        <div className="sub-view">
            <PendingSummaryCard pending={pending} t={t} />

            <PayWindowNotice dueAt={pending.paymentDueAt} lang={lang} t={t} />

            <p className="sub-section-label">{t.subscriptions.pendingSubtitle}</p>

            {slug && <SlugCopyRow slug={slug} t={t} />}

            <div className="sub-payment-methods-list">
                {config.paymentReceivers.map((r) => (
                    <PaymentMethodCard key={r.method} receiver={r} t={t} />
                ))}
            </div>

            <a className="sub-whatsapp-btn" href={whatsAppUrl} target="_blank" rel="noopener noreferrer">
                {WHATSAPP_SVG}
                {t.subscriptions.sendReceiptButton}
            </a>

            <p className="sub-pending-note">{t.subscriptions.pendingNote}</p>
        </div>
    );
}

// --- Current Plan View --------------------------------------------------------------------------

type CurrentPlanViewProps = {
    current: TySubscription;
    pending: TySubscription | null;
    coming: TySubscription | null;
    config: TySubscriptionConfig;
    lang: string;
    t: ReturnType<typeof useTranslation>;
    onRenew: () => void;
    onAddBranches: () => void;
    onPayAddon: (addon: TyBranchAddonItem) => void;
    onBranchConfig: () => void;
    store: TyActiveStore | null;
};

function CurrentPlanView({
    current,
    pending,
    coming,
    config,
    lang,
    t,
    onRenew,
    onAddBranches,
    onPayAddon,
    onBranchConfig,
    store,
}: CurrentPlanViewProps) {
    const PERIOD_LABELS: Record<TySubscriptionPeriod, string> = {
        monthly: t.subscriptions.periodMonthly,
        quarterly: t.subscriptions.periodQuarterly,
        annually: t.subscriptions.periodAnnually,
    };

    const isGrace = current.status === "grace";
    const isPaused = current.status === "paused";
    const isActive = current.status === "active";

    const daysLeft = daysRemaining(current.endDate);
    const graceEnd = (() => {
        if (!current.endDate) return 0;
        const end = new Date(current.endDate);
        end.setDate(end.getDate() + 5);
        return daysRemaining(end.toISOString());
    })();

    const PERIOD_DAYS: Record<TySubscriptionPeriod, number> = { monthly: 30, quarterly: 90, annually: 365 };
    const barPercent = Math.min(100, Math.max(0, (daysLeft / PERIOD_DAYS[current.period]) * 100));
    const barColor = barPercent > 60 ? "#3fac89" : barPercent > 25 ? "#d99e29" : "#cf4c4c";
    const graceDaysPassed = current.endDate ? Math.max(0, Math.floor((Date.now() - new Date(current.endDate).getTime()) / 86_400_000)) : 0;
    const GRACE_TOTAL = graceEnd + graceDaysPassed;
    const graceOffDate = current.endDate
        ? (() => {
              const d = new Date(current.endDate);
              d.setDate(d.getDate() + GRACE_TOTAL);
              return d.toISOString();
          })()
        : null;

    const comingEstStart = current.endDate ?? null;
    const comingEstEnd =
        current.endDate && coming
            ? (() => {
                  const d = new Date(current.endDate!);
                  d.setDate(d.getDate() + PERIOD_DAYS[coming.period]);
                  return d.toISOString();
              })()
            : null;

    const slug = store?.slug ?? "";
    const whatsAppPhone = config.whatsappPhone.replace("+", "");

    return (
        <div className="sub-view">
            {isGrace && !pending && (
                <div className="sub-alert sub-alert--danger">
                    <img src="/icons/close-icon.svg" alt="" width={20} height={20} />
                    <div>
                        <p className="sub-alert-title">{t.subscriptions.graceTitle}</p>
                        <p className="sub-alert-note">
                            {t.subscriptions.graceNote} <strong>{graceEnd}</strong> {t.subscriptions.graceDays}
                        </p>
                    </div>
                </div>
            )}

            {isPaused && !pending && (
                <div className="sub-alert sub-alert--danger">
                    <img src="/icons/close-icon.svg" alt="" width={20} height={20} />
                    <div>
                        <p className="sub-alert-title">{t.subscriptions.pausedTitle}</p>
                        <p className="sub-alert-note">{t.subscriptions.pausedNote}</p>
                    </div>
                </div>
            )}

            <div className="sub-current-card">
                <div className="sub-card-badge-row">
                    <StatusBadge status={current.status} t={t} />
                    <span className="sub-current-period-badge">{PERIOD_LABELS[current.period]}</span>
                </div>

                {isActive && (
                    <>
                        <div className="sub-days-hero">
                            <span className="sub-days-hero-number" style={{ color: barColor }}>
                                {daysLeft}
                            </span>
                            <span className="sub-days-hero-label">{t.subscriptions.daysLeft}</span>
                        </div>
                        <div className="sub-days-bar">
                            <div className="sub-days-bar-fill" style={{ width: `${barPercent}%`, backgroundColor: barColor }} />
                        </div>
                    </>
                )}

                {isGrace && (
                    <>
                        <div className="sub-days-hero">
                            <span className="sub-days-hero-number" style={{ color: "#cf4c4c" }}>
                                {graceEnd}
                            </span>
                            <span className="sub-days-hero-label">{t.subscriptions.graceDaysLabel}</span>
                        </div>
                        <div className="sub-grace-calendar">
                            {Array.from({ length: GRACE_TOTAL }, (_, i) => {
                                const isPassed = i < graceDaysPassed;
                                return (
                                    <div key={i} className={`sub-grace-day${isPassed ? " sub-grace-day--passed" : ""}`}>
                                        {isPassed ? "✕" : ""}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                <div className="sub-card-divider" />

                <div className="sub-meta-row">
                    <div className="sub-meta-item">
                        <span className="sub-meta-label">{t.subscriptions.periodLabel}</span>
                        <span className="sub-meta-value">{PERIOD_LABELS[current.period]}</span>
                    </div>
                    <div className="sub-meta-sep" />
                    <div className="sub-meta-item">
                        <span className="sub-meta-label">{t.subscriptions.branchesLabel}</span>
                        <span className="sub-meta-value">
                            {current.branches} {t.subscriptions.branchCount}
                        </span>
                    </div>
                    <div className="sub-meta-sep" />
                    <div className="sub-meta-item">
                        <span className="sub-meta-label">{t.subscriptions.planPrice}</span>
                        <span className="sub-meta-value">
                            {current.price} {t.subscriptions.egp}
                        </span>
                    </div>
                </div>

                {isGrace
                    ? current.endDate && (
                          <div className="sub-date-cards">
                              <div className="sub-date-card sub-date-card--ended">
                                  <span className="sub-date-card-label">{t.subscriptions.graceEndedLabel}</span>
                                  <span className="sub-date-card-value">{formatDate(current.endDate, lang)}</span>
                              </div>
                              {graceOffDate && (
                                  <div className="sub-date-card sub-date-card--off">
                                      <span className="sub-date-card-label">{t.subscriptions.graceOffLabel}</span>
                                      <span className="sub-date-card-value">{formatDate(graceOffDate, lang)}</span>
                                  </div>
                              )}
                          </div>
                      )
                    : current.startDate &&
                      current.endDate && (
                          <div className="sub-date-cards">
                              <div className="sub-date-card">
                                  <span className="sub-date-card-label">{t.subscriptions.startDateLabel}</span>
                                  <span className="sub-date-card-value">{formatDate(current.startDate, lang)}</span>
                              </div>
                              <div className="sub-date-card">
                                  <span className="sub-date-card-label">{t.subscriptions.endDateLabel}</span>
                                  <span className="sub-date-card-value">{formatDate(current.endDate, lang)}</span>
                              </div>
                          </div>
                      )}
            </div>

            <div className="sub-branch-actions-row">
                {isActive && (
                    <button className="sub-add-branches-btn" type="button" onClick={onAddBranches}>
                        {t.branchAddon.addBranchesButton}
                    </button>
                )}
                <button className="sub-branch-config-btn" type="button" onClick={onBranchConfig}>
                    {t.subscriptions.branchConfigButton}
                </button>
            </div>

            {coming && (
                <div className="sub-coming-card">
                    <div className="sub-card-badge-row">
                        <StatusBadge status="coming" t={t} />
                    </div>
                    <p className="sub-coming-note">{t.subscriptions.comingNote}</p>
                    <div className="sub-coming-details">
                        <div className="sub-coming-detail-item">
                            <span className="sub-coming-detail-label">{t.subscriptions.periodLabel}</span>
                            <span className="sub-coming-detail-value">{PERIOD_LABELS[coming.period]}</span>
                        </div>
                        <div className="sub-coming-detail-item">
                            <span className="sub-coming-detail-label">{t.subscriptions.branchesLabel}</span>
                            <span className="sub-coming-detail-value">
                                {coming.branches} {t.subscriptions.branchCount}
                            </span>
                        </div>
                        <div className="sub-coming-detail-item">
                            <span className="sub-coming-detail-label">{t.subscriptions.planPrice}</span>
                            <span className="sub-coming-detail-value">
                                {coming.price} {t.subscriptions.egp}
                            </span>
                        </div>
                    </div>

                    {comingEstStart && comingEstEnd && (
                        <div className="sub-date-cards">
                            <div className="sub-date-card">
                                <span className="sub-date-card-label">{t.subscriptions.comingStartLabel}</span>
                                <span className="sub-date-card-value">{formatDate(comingEstStart, lang)}</span>
                            </div>
                            <div className="sub-date-card">
                                <span className="sub-date-card-label">{t.subscriptions.comingEndLabel}</span>
                                <span className="sub-date-card-value">{formatDate(comingEstEnd, lang)}</span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {pending && (
                <>
                    <div className="sub-pending-renewal-card">
                        <div className="sub-pending-renewal-icon">
                            <img src="/icons/check-icon.svg" alt="" width={18} height={18} />
                        </div>
                        <div>
                            <p className="sub-pending-renewal-title">{t.subscriptions.pendingRenewalTitle}</p>
                            <p className="sub-pending-renewal-note">{t.subscriptions.pendingRenewalNote}</p>
                        </div>
                    </div>

                    <p className="sub-section-label">{t.subscriptions.pendingSubtitle}</p>
                    {slug && <SlugCopyRow slug={slug} t={t} />}
                    <div className="sub-payment-methods-list">
                        {config.paymentReceivers.map((r) => (
                            <PaymentMethodCard key={r.method} receiver={r} t={t} />
                        ))}
                    </div>
                    <a
                        className="sub-whatsapp-btn"
                        href={`https://wa.me/${whatsAppPhone}?text=${encodeURIComponent(`طلب تفعيل تجديد اشتراك منيوكيت\nرابط المتجر: ${slug}\nالباقة: ${PERIOD_LABELS[pending.period]}\nالفروع: ${pending.branches} فرع\nالمبلغ: ${pending.price} ${t.subscriptions.egp}`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {WHATSAPP_SVG}
                        {t.subscriptions.sendReceiptButton}
                    </a>
                </>
            )}

            {(() => {
                const pendingAddons = (current.branchesAddon ?? []).filter((a) => a.status === "pending");
                if (pendingAddons.length === 0) return null;
                const totalBranches = pendingAddons.reduce((s, a) => s + a.branches, 0);
                const totalAmount = pendingAddons.reduce((s, a) => s + a.activeAmount + a.comingAmount, 0);
                const latest = pendingAddons[pendingAddons.length - 1];
                return (
                    <div className="sub-addon-pending-banner">
                        <div className="sub-addon-pending-icon">
                            <img src="/icons/info-circle-icon.svg" alt="" width={16} height={16} />
                        </div>
                        <div className="sub-addon-pending-text">
                            <span className="sub-addon-pending-branches">
                                +{totalBranches} {t.subscriptions.branchCount}
                            </span>
                            <span className="sub-addon-pending-amount">
                                {totalAmount.toLocaleString()} {t.branchAddon.egp}
                            </span>
                        </div>
                        <button className="sub-addon-details-btn" type="button" onClick={() => onPayAddon(latest)}>
                            {t.branchAddon.payNowButton}
                        </button>
                    </div>
                );
            })()}

            {!pending && !coming && (
                <button className="sub-renew-btn" type="button" onClick={onRenew}>
                    {isPaused ? t.subscriptions.reactivate : isGrace ? t.subscriptions.renewNow : t.subscriptions.renewButton}
                </button>
            )}
        </div>
    );
}

// --- Main Screen --------------------------------------------------------------------------------

type SubscriptionData = {
    current: TySubscription | null;
    pending: TySubscription | null;
    coming: TySubscription | null;
    /** Branches on the last plan that ran, so a renewal reopens on what the store already pays for. */
    lastBranches: number | null;
    /** Whether this store has ever held a plan — decides which coupons the preview may accept. */
    hasHistory: boolean;
};

export default function SubscriptionsScreen() {
    const t = useTranslation();
    const lang = useLang((s) => s.lang) ?? "ar";
    const store: TyActiveStore | null = useStore((s) => s.store);
    const { addComp } = usePopup();
    const navigate = useNavigate();
    const [showRenewForm, setShowRenewForm] = useState(false);

    const { data: configData, loading: configLoading } = useFetch<TyApiResponse<TySubscriptionConfig>>("/subscriptions/config");
    const { data: subData, loading: subLoading, invoke: refetchSub } = useFetch<TyApiResponse<SubscriptionData>>("/subscriptions/current");

    const config = configData?.data ?? null;
    const current = subData?.data?.current ?? null;
    const pending = subData?.data?.pending ?? null;
    const coming = subData?.data?.coming ?? null;
    /* The live plan first, then the last one that ran — a store that let its plan end still renews on
       the branches it had, rather than being dropped back to one. */
    const initialBranches = current?.branches ?? subData?.data?.lastBranches ?? 1;
    const hasHistory = subData?.data?.hasHistory ?? false;

    const isLoading = configLoading || subLoading;

    const handleSubscribeSuccess = () => {
        setShowRenewForm(false);
        refetchSub();
    };

    const openAddonPopup = () => {
        addComp({
            name: "purchase-branch-addon",
            Component: PurchaseBranchAddonPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details: { onSuccess: refetchSub },
        });
    };

    const openAddonPaymentPopup = (addon: TyBranchAddonItem) => {
        addComp({
            name: `pay-branch-addon-${addon._id}`,
            Component: PurchaseBranchAddonPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details: {
                onSuccess: refetchSub,
                existingAddon: {
                    branches: addon.branches,
                    activeAmount: addon.activeAmount,
                    comingAmount: addon.comingAmount,
                },
            },
        });
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="sub-view">
                    <Skeleton className="sub-skeleton-hero" />
                    <div className="sub-plan-grid">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} className="sub-skeleton-plan" />
                        ))}
                    </div>
                    <Skeleton className="sub-skeleton-row" />
                    <Skeleton className="sub-skeleton-row" />
                    <Skeleton className="sub-skeleton-submit" />
                </div>
            );
        }

        if (!config) return null;

        if (showRenewForm) {
            return (
                <>
                    <button className="sub-back-btn" type="button" onClick={() => setShowRenewForm(false)}>
                        <img src="/icons/arrow-dark-icon.svg" alt="" width={16} height={16} />
                    </button>
                    <PlanSelectorView config={config} t={t} onSuccess={handleSubscribeSuccess} initialBranches={initialBranches} isRenewal />
                </>
            );
        }

        if (!current && !pending) {
            return (
                <PlanSelectorView config={config} t={t} onSuccess={handleSubscribeSuccess} initialBranches={initialBranches} isRenewal={hasHistory} />
            );
        }

        if (!current && pending) {
            return <PaymentInstructionsView pending={pending} config={config} t={t} store={store} lang={lang} />;
        }

        if (current) {
            return (
                <CurrentPlanView
                    current={current}
                    pending={pending}
                    coming={coming}
                    config={config}
                    lang={lang}
                    t={t}
                    onRenew={() => setShowRenewForm(true)}
                    onAddBranches={openAddonPopup}
                    onPayAddon={openAddonPaymentPopup}
                    onBranchConfig={() => navigate("/subscriptions/branches")}
                    store={store}
                />
            );
        }

        return null;
    };

    return (
        <div className="subscriptions-screen">
            <div className="subscriptions-screen-content">{renderContent()}</div>
        </div>
    );
}
